/**
 * ToastNotification — Stackable toast notifications for the MMO HUD.
 * Appears top-right, auto-dismisses after duration, supports click-to-dismiss.
 * Uses a static manager to handle stacking and max-visible limits.
 */

import Phaser from 'phaser';
import { Colors, CSSColors, Fonts, FontSize, Radius, Depth, Anim } from './GameUITheme';

// ── Relic tier colors ──────────────────────────────────────────
export const TIER_COLORS: Record<number, { hex: number; css: string; label: string }> = {
  1: { hex: 0xffffff, css: '#ffffff', label: 'T1' },
  2: { hex: 0x44ff44, css: '#44ff44', label: 'T2' },
  3: { hex: 0x4488ff, css: '#4488ff', label: 'T3' },
  4: { hex: 0xaa44ff, css: '#aa44ff', label: 'T4' },
  5: { hex: 0xffcc00, css: '#ffcc00', label: 'T5' },
};

export interface ToastOptions {
  color?: number;
  duration?: number;
  icon?: string;
}

const TOAST_W = 320;
const TOAST_H = 48;
const TOAST_PAD = 12;
const TOAST_GAP = 6;
const MAX_VISIBLE = 5;
const DEFAULT_DURATION = 5000;
const BORDER_W = 4;

// Static stack of active toasts, managed across instances
const activeToasts: ToastNotification[] = [];

export class ToastNotification extends Phaser.GameObjects.Container {
  private dismissTimer?: Phaser.Time.TimerEvent;
  private dismissed = false;

  constructor(
    scene: Phaser.Scene,
    message: string,
    options: ToastOptions = {},
  ) {
    const { width } = scene.cameras.main;
    const startX = width - TOAST_W - TOAST_PAD;
    const startY = TOAST_PAD;

    super(scene, startX, startY);

    const accentColor = options.color ?? Colors.cyan;
    const duration = options.duration ?? DEFAULT_DURATION;

    // ── Background ──────────────────────────────────────────
    const bg = scene.add.graphics();
    bg.fillStyle(Colors.bgCard, 0.92);
    bg.fillRoundedRect(0, 0, TOAST_W, TOAST_H, Radius.sm);
    bg.lineStyle(1, Colors.borderDim, 0.3);
    bg.strokeRoundedRect(0, 0, TOAST_W, TOAST_H, Radius.sm);
    this.add(bg);

    // ── Colored left border accent ──────────────────────────
    const accent = scene.add.graphics();
    accent.fillStyle(accentColor, 1);
    accent.fillRoundedRect(0, 0, BORDER_W, TOAST_H, { tl: Radius.sm, bl: Radius.sm, tr: 0, br: 0 });
    this.add(accent);

    // ── Icon (optional) ─────────────────────────────────────
    let textX = BORDER_W + 10;
    if (options.icon) {
      const iconText = scene.add.text(BORDER_W + 10, TOAST_H / 2, options.icon, {
        fontSize: FontSize.md,
        color: CSSColors.textWhite,
      });
      iconText.setOrigin(0, 0.5);
      this.add(iconText);
      textX = BORDER_W + 10 + iconText.width + 8;
    }

    // ── Message text ────────────────────────────────────────
    const msgText = scene.add.text(textX, TOAST_H / 2, message, {
      fontSize: FontSize.sm,
      color: CSSColors.textWhite,
      fontFamily: Fonts.body,
      wordWrap: { width: TOAST_W - textX - 10 },
    });
    msgText.setOrigin(0, 0.5);
    this.add(msgText);

    // ── Interactive hit zone (click to dismiss) ─────────────
    const hitZone = scene.add.zone(0, 0, TOAST_W, TOAST_H).setOrigin(0, 0);
    hitZone.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, TOAST_W, TOAST_H),
      Phaser.Geom.Rectangle.Contains,
    );
    hitZone.on('pointerover', () => {
      scene.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      scene.input.setDefaultCursor('default');
    });
    hitZone.on('pointerdown', () => {
      this.dismiss();
    });
    this.add(hitZone);

    // ── Depth & scroll factor ───────────────────────────────
    this.setDepth(Depth.overlay);
    this.setScrollFactor(0);

    // ── Slide-in animation ──────────────────────────────────
    this.setAlpha(0);
    this.x = startX + 40;
    scene.tweens.add({
      targets: this,
      x: startX,
      alpha: 1,
      duration: Anim.normal,
      ease: 'Back.easeOut',
    });

    // ── Auto-dismiss timer ──────────────────────────────────
    this.dismissTimer = scene.time.delayedCall(duration, () => {
      this.dismiss();
    });

    // ── Add to scene ────────────────────────────────────────
    scene.add.existing(this);
  }

  /**
   * Dismiss this toast with a fade-out animation.
   */
  dismiss(): void {
    if (this.dismissed) return;
    this.dismissed = true;

    if (this.dismissTimer) {
      this.dismissTimer.remove(false);
      this.dismissTimer = undefined;
    }

    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      x: this.x + 40,
      duration: Anim.fast,
      ease: 'Quad.easeIn',
      onComplete: () => {
        // Remove from active stack
        const idx = activeToasts.indexOf(this);
        if (idx !== -1) activeToasts.splice(idx, 1);
        // Reposition remaining toasts
        ToastNotification.repositionToasts(this.scene);
        this.destroy();
      },
    });
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Static manager methods
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /**
   * Show a new toast notification, handling stacking and max-visible limits.
   */
  static show(scene: Phaser.Scene, message: string, options: ToastOptions = {}): ToastNotification {
    // Enforce max visible — dismiss oldest if exceeded
    while (activeToasts.length >= MAX_VISIBLE) {
      const oldest = activeToasts.shift();
      oldest?.dismiss();
    }

    const toast = new ToastNotification(scene, message, options);
    activeToasts.push(toast);

    // Reposition all toasts to stack from top
    ToastNotification.repositionToasts(scene);

    return toast;
  }

  /**
   * Reposition all active toasts vertically from the top-right.
   * Each new toast pushes existing ones down.
   */
  private static repositionToasts(scene: Phaser.Scene): void {
    const { width } = scene.cameras.main;
    const startX = width - TOAST_W - TOAST_PAD;

    for (let i = 0; i < activeToasts.length; i++) {
      const toast = activeToasts[i];
      if (toast.dismissed) continue;

      const targetY = TOAST_PAD + i * (TOAST_H + TOAST_GAP);

      scene.tweens.add({
        targets: toast,
        x: startX,
        y: targetY,
        duration: Anim.fast,
        ease: 'Quad.easeOut',
      });
    }
  }

  /**
   * Dismiss all active toasts.
   */
  static dismissAll(): void {
    const toasts = [...activeToasts];
    for (const toast of toasts) {
      toast.dismiss();
    }
  }
}
